import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format, parse } from "date-fns";
import { 
  ChevronLeft, 
  ChevronRight, 
  Save, 
  X, 
  AlertCircle,
  RefreshCw,
  Calendar
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatCurrencyInput, parseCurrencyInput } from "../utils/currencyFormat";

import LastUpdated from "../components/LastUpdated";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const SalariesPage = () => {
  const { yearParam } = useParams();
  const navigate = useNavigate();
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth(); // 0-indexed
  
  const [year, setYear] = useState(() => {
    if (yearParam) {
      const parsed = parseInt(yearParam);
      if (!isNaN(parsed) && parsed >= 2000 && parsed <= 2100) {
        return parsed;
      }
    }
    return currentYear;
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [originalData, setOriginalData] = useState([]);
  const [formData, setFormData] = useState([]);

  useEffect(() => { document.title = "Salaries - Floatly"; }, []);

  // Update URL when year changes
  useEffect(() => {
    const newPath = `/salaries/${year}`;
    if (window.location.pathname !== newPath) {
      navigate(newPath, { replace: true });
    }
  }, [year, navigate]);

  // Fetch data for year
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/salaries/year/${year}`);
      const entries = response.data.entries;
      
      setOriginalData(JSON.parse(JSON.stringify(entries)));
      setFormData(entries);
    } catch (err) {
      console.error("Error fetching salary data:", err);
      setError("Failed to load salary data");
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Check if there are changes
  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);

  // Navigation
  const goToPrevYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(y => y - 1);
  };

  const goToNextYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(y => y + 1);
  };

  const goToCurrentYear = () => {
    if (hasChanges && !window.confirm("You have unsaved changes. Discard and navigate?")) {
      return;
    }
    setYear(currentYear);
  };

  // Handle input changes
  const handleChange = (monthIndex, field, value) => {
    setFormData(prev => {
      const newData = [...prev];
      newData[monthIndex] = { ...newData[monthIndex], [field]: value };
      return newData;
    });
  };

  // Save data
  const handleSave = async () => {
    if (!hasChanges) return;
    
    // Validate all entries
    for (const entry of formData) {
      const salaryAmount = parseFloat(entry.salary_amount) || 0;
      const taxAmount = parseFloat(entry.tax_amount) || 0;
      
      if (salaryAmount < 0 || salaryAmount > 999999.99) {
        toast.error(`Invalid salary amount for ${entry.month}`);
        return;
      }
      if (taxAmount < 0 || taxAmount > 500000) {
        toast.error(`Invalid tax amount for ${entry.month}`);
        return;
      }
    }
    
    setSaving(true);
    try {
      await axios.patch(`${API}/salaries/year/${year}`, {
        entries: formData.map(entry => ({
          month: entry.month,
          salary_amount: parseFloat(entry.salary_amount) || 0,
          salary_payment_date: entry.salary_payment_date,
          tax_amount: parseFloat(entry.tax_amount) || 0,
          tax_payment_date: entry.tax_payment_date
        }))
      });
      
      setOriginalData(JSON.parse(JSON.stringify(formData)));
      toast.success("Salary data saved successfully");
    } catch (err) {
      console.error("Error saving salary data:", err);
      toast.error("Failed to save salary data");
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    setFormData(JSON.parse(JSON.stringify(originalData)));
    toast.info("Changes discarded");
  };

  // Format currency for display
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat("nl-NL", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  // Calculate totals
  const totalSalary = formData.reduce((sum, entry) => sum + (parseFloat(entry.salary_amount) || 0), 0);
  const totalTax = formData.reduce((sum, entry) => sum + (parseFloat(entry.tax_amount) || 0), 0);
  const totalOutflow = totalSalary + totalTax;

  // Check if month is current or past
  const isCurrentMonth = (monthIndex) => year === currentYear && monthIndex === currentMonth;
  const isPastMonth = (monthIndex) => year < currentYear || (year === currentYear && monthIndex < currentMonth);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="error-state">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-2xl font-semibold mb-2">Something went wrong</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={fetchData} data-testid="retry-button">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="salaries-page">
      {/* Header */}
      <header className="border-b bg-white shrink-0">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="page-title">
                Salaries & Payroll Tax
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Monthly salary and tax payment management
              </p>
              <LastUpdated page="salaries" />
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Year Total</p>
              <p className="text-2xl font-semibold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {formatCurrency(totalOutflow)}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Year Navigator */}
      <div className="border-b bg-white/80 backdrop-blur sticky top-0 z-30 shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={goToPrevYear}
                className="rounded-full"
                data-testid="prev-year-button"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <h2 className="text-xl font-semibold min-w-[100px] text-center" data-testid="current-year">
                {year}
              </h2>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={goToNextYear}
                className="rounded-full"
                data-testid="next-year-button"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground hidden md:block">
                Salary: {formatCurrency(totalSalary)} • Tax: {formatCurrency(totalTax)}
              </div>
              {year !== currentYear && (
                <Button
                  variant="outline"
                  onClick={goToCurrentYear}
                  className="rounded-full"
                  data-testid="current-year-button"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {currentYear}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Year Table */}
      <main className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="loading-state">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="bg-white border rounded-xl shadow-sm overflow-auto" style={{ maxHeight: "calc(100vh - 280px)", paddingBottom: "40px" }}>
            <table className="w-full" data-testid="salaries-table">
              <thead className="bg-white border-b sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[140px]">
                    Month
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[150px]">
                    Salary Amount
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[140px]">
                    Salary Date
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[150px]">
                    Tax Amount
                  </th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[140px]">
                    Tax Date
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-[130px]">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {formData.map((entry, index) => {
                  const monthTotal = (parseFloat(entry.salary_amount) || 0) + (parseFloat(entry.tax_amount) || 0);
                  const isCurrent = isCurrentMonth(index);
                  const isPast = isPastMonth(index);
                  
                  return (
                    <tr 
                      key={entry.month}
                      className={`
                        border-b last:border-b-0 transition-colors
                        ${isCurrent ? "bg-primary/5" : ""}
                        ${isPast ? "bg-muted/20" : ""}
                        hover:bg-muted/30
                      `}
                      data-testid={`row-${entry.month}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`font-medium ${isCurrent ? "text-primary" : ""}`}>
                            {MONTHS[index]}
                          </span>
                          {isCurrent && (
                            <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                              Current
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={formatCurrencyInput(entry.salary_amount)}
                            onChange={(e) => handleChange(index, "salary_amount", parseCurrencyInput(e.target.value))}
                            onFocus={(e) => {
                              const val = entry.salary_amount || 0;
                              e.target.value = val !== 0 ? val.toString().replace(".", ",") : "";
                              e.target.select();
                            }}
                            onBlur={(e) => {
                              e.target.value = formatCurrencyInput(parseCurrencyInput(e.target.value));
                            }}
                            className="pl-6 text-right font-mono h-9 text-sm"
                            placeholder="0"
                            data-testid={`salary-amount-${index}`}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="date"
                          value={entry.salary_payment_date || ""}
                          onChange={(e) => handleChange(index, "salary_payment_date", e.target.value)}
                          className="font-mono h-9 text-sm text-center"
                          data-testid={`salary-date-${index}`}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={formatCurrencyInput(entry.tax_amount)}
                            onChange={(e) => handleChange(index, "tax_amount", parseCurrencyInput(e.target.value))}
                            onFocus={(e) => {
                              const val = entry.tax_amount || 0;
                              e.target.value = val !== 0 ? val.toString().replace(".", ",") : "";
                              e.target.select();
                            }}
                            onBlur={(e) => {
                              e.target.value = formatCurrencyInput(parseCurrencyInput(e.target.value));
                            }}
                            className="pl-6 text-right font-mono h-9 text-sm"
                            placeholder="0"
                            data-testid={`tax-amount-${index}`}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="date"
                          value={entry.tax_payment_date || ""}
                          onChange={(e) => handleChange(index, "tax_payment_date", e.target.value)}
                          className="font-mono h-9 text-sm text-center"
                          data-testid={`tax-date-${index}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono font-medium text-sm">
                          {formatCurrency(monthTotal)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted/50 border-t-2">
                <tr>
                  <td className="px-4 py-3 font-semibold">Year Total</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">
                    {formatCurrency(totalSalary)}
                  </td>
                  <td></td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">
                    {formatCurrency(totalTax)}
                  </td>
                  <td></td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-lg">
                    {formatCurrency(totalOutflow)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </main>

      {/* Floating Action Bar */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white border rounded-full shadow-lg px-6 py-3 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-4" data-testid="action-bar">
          <span className="text-sm text-muted-foreground">
            Unsaved changes
          </span>
          <Button variant="outline" onClick={handleDiscard} className="rounded-full" data-testid="discard-button">
            <X className="w-4 h-4 mr-2" />
            Discard
          </Button>
          <Button onClick={handleSave} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="save-button">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Year"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SalariesPage;
